// TP Club Radar API client — the club's shared watchlist (P3/P4).
//
// Own axios instance, isolated from services/api.js and from agentRadarService
// (Scope Law: the agent radar client is never touched) — same Bearer-token
// pattern as scoutRequestService / agentRadarService. Paths are the fixed
// API-CONTRACT under the dashboard mount, router prefix `club/radar/`:
//   lists            → /dashboard/club/radar/lists/[{id}/][sharing|members|share]
//   entries          → /dashboard/club/radar/entries/[{id}/][status|move|copy|
//                       timeline|notes|labels|contact-recommender]
//   labels           → /dashboard/club/radar/labels/
//   profile-status   → /dashboard/club/radar/profile-status/
//   save-from-profile→ /dashboard/club/radar/save-from-profile/
//   snapshots        → /dashboard/club/radar/snapshots/{id}/
// Everything club-scoped + access_radar-gated server-side (R9).
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_ENDPOINT;

const authApi = axios.create({ baseURL: API_BASE_URL, withCredentials: true });

authApi.interceptors.request.use((config) => {
  try {
    const tokens = JSON.parse(localStorage.getItem("tokens") || "null");
    if (tokens?.access) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${tokens.access}`;
    }
  } catch (e) {
    /* no token — request proceeds and the server returns 401 */
  }
  return config;
});

const fail = (error) => ({
  success: false,
  status: error?.response?.status,
  error:
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    (typeof error?.response?.data === "object"
      ? Object.values(error.response.data)[0]
      : null) ||
    "Something went wrong. Please try again.",
});

const isCancel = (error) => error?.name === "CanceledError" || error?.name === "AbortError";

// List endpoints may return a bare array or a DRF paginated envelope.
const asList = (data) => (Array.isArray(data) ? data : data?.results || []);

// The BE nests the per-entry status + its attribution under a `status` object
// ({ status, status_set_by_name, status_set_by_role, status_set_at }). The FE
// (StatusPill, filter counts, ClubEntryDetail) consumes a flat shape, so flatten
// it here at the boundary — a string `status` plus the attribution fields.
const normalizeEntry = (e) => {
  if (!e || typeof e !== "object") return e;
  const s = e.status;
  if (s && typeof s === "object") {
    return {
      ...e,
      status: s.status,
      status_set_by_name: s.status_set_by_name,
      status_set_by_role: s.status_set_by_role,
      status_set_at: s.status_set_at,
    };
  }
  return e;
};

const BASE = "/dashboard/club/radar";

export const clubRadarService = {
  // ── Lists ──────────────────────────────────────────────────────────────────
  // → { my_lists: [...], shared_with_club: [...] }. Tolerant of a bare array
  //   (older BE) by placing it all under my_lists.
  getLists: async (signal) => {
    try {
      const res = await authApi.get(`${BASE}/lists/`, { signal });
      const d = res.data || {};
      const data = Array.isArray(d)
        ? { my_lists: d, shared_with_club: [] }
        : { my_lists: asList(d.my_lists), shared_with_club: asList(d.shared_with_club) };
      return { success: true, data };
    } catch (error) {
      if (isCancel(error)) throw error;
      return fail(error);
    }
  },

  createList: async ({ name, description } = {}) => {
    try {
      const body = { name };
      if (description != null) body.description = description;
      const res = await authApi.post(`${BASE}/lists/`, body);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // PATCH name and/or description — creator/ClubAdmin only server-side (R6).
  updateList: async (id, patch) => {
    try {
      const res = await authApi.patch(`${BASE}/lists/${id}/`, patch);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // Soft-delete a custom list; the server moves its orphaned entries to the
  // creator's My Radar, keeping notes/labels/statuses (frame 1p).
  deleteList: async (id) => {
    try {
      const res = await authApi.delete(`${BASE}/lists/${id}/`);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // PUT the sharing tier (R4). member_ids only for `selected`.
  setSharing: async (id, { shareTier, memberIds }) => {
    try {
      const body = { share_tier: shareTier };
      if (shareTier === "selected") body.member_ids = memberIds || [];
      const res = await authApi.put(`${BASE}/lists/${id}/sharing/`, body);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // Club colleagues with access_radar, for the Selected-people multi-select (2a).
  getListMembers: async (id, signal) => {
    try {
      const res = await authApi.get(`${BASE}/lists/${id}/members/`, { signal });
      return { success: true, data: asList(res.data) };
    } catch (error) {
      if (isCancel(error)) throw error;
      return fail(error);
    }
  },

  // Share-to-chat (R5): read-only snapshot card into chat channels.
  shareList: async (id, { channelIds, message }) => {
    try {
      const res = await authApi.post(`${BASE}/lists/${id}/share/`, {
        channel_ids: channelIds,
        message: message || "",
      });
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // ── Entries ─────────────────────────────────────────────────────────────────
  // params: { list, status, from_scout_requests, label, sort }
  getEntries: async (params, signal) => {
    try {
      const res = await authApi.get(`${BASE}/entries/`, { params, signal });
      return { success: true, data: asList(res.data).map(normalizeEntry) };
    } catch (error) {
      if (isCancel(error)) throw error;
      return fail(error);
    }
  },

  getEntry: async (id, signal) => {
    try {
      const res = await authApi.get(`${BASE}/entries/${id}/`, { signal });
      return { success: true, data: normalizeEntry(res.data) };
    } catch (error) {
      if (isCancel(error)) throw error;
      return fail(error);
    }
  },

  // Remove a player FROM a list (soft). Destructive → creator/ClubAdmin (R6).
  removeEntry: async (id, listId) => {
    try {
      const res = await authApi.delete(`${BASE}/entries/${id}/`, {
        params: listId ? { list: listId } : undefined,
      });
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // Set the club-visible status (R7). Any member with access; writes a timeline event.
  setStatus: async (id, status) => {
    try {
      const res = await authApi.post(`${BASE}/entries/${id}/status/`, { status });
      return { success: true, data: normalizeEntry(res.data) };
    } catch (error) {
      return fail(error);
    }
  },

  // Move OUT of `fromListId` INTO `toListId` (Move = Remove + Add; creator/ClubAdmin).
  moveEntry: async (id, toListId, fromListId) => {
    try {
      const body = { to_list_id: toListId };
      if (fromListId) body.from_list_id = fromListId;
      const res = await authApi.post(`${BASE}/entries/${id}/move/`, body);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  copyEntry: async (id, toListId) => {
    try {
      const res = await authApi.post(`${BASE}/entries/${id}/copy/`, { to_list_id: toListId });
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  getTimeline: async (id, signal) => {
    try {
      const res = await authApi.get(`${BASE}/entries/${id}/timeline/`, { signal });
      return { success: true, data: asList(res.data) };
    } catch (error) {
      if (isCancel(error)) throw error;
      return fail(error);
    }
  },

  addNote: async (id, text) => {
    try {
      const res = await authApi.post(`${BASE}/entries/${id}/notes/`, { text });
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  assignLabel: async (id, labelId) => {
    try {
      const res = await authApi.post(`${BASE}/entries/${id}/labels/`, { label_id: labelId });
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  unassignLabel: async (id, labelId) => {
    try {
      const res = await authApi.delete(`${BASE}/entries/${id}/labels/${labelId}/`);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // Opens/fetches the normal club→agent chat channel (R15).
  contactRecommender: async (id) => {
    try {
      const res = await authApi.post(`${BASE}/entries/${id}/contact-recommender/`);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // ── Labels (club-wide palette) ───────────────────────────────────────────────
  getLabels: async (signal) => {
    try {
      const res = await authApi.get(`${BASE}/labels/`, { signal });
      return { success: true, data: asList(res.data) };
    } catch (error) {
      if (isCancel(error)) throw error;
      return fail(error);
    }
  },

  createLabel: async ({ name, color }) => {
    try {
      const res = await authApi.post(`${BASE}/labels/`, { name, color });
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // ── Profile header button + picker (R10) ─────────────────────────────────────
  // params: { ws_player_id } or { player_id } → { on_radar, lists:[{id,name,...,already_in}] }
  getProfileStatus: async (params, signal) => {
    try {
      const res = await authApi.get(`${BASE}/profile-status/`, { params, signal });
      return { success: true, data: res.data };
    } catch (error) {
      if (isCancel(error)) throw error;
      return fail(error);
    }
  },

  // Idempotent upsert to the given lists with optional status + note (frame 1l).
  // NOT for scout-request recommendations (those keep the existing add_to_radar path).
  // tsCoachId (Coach-Radar Phase 2) keys a coach entry — exactly one key.
  saveFromProfile: async ({ wsPlayerId, playerId, tsCoachId, listIds, status, note }) => {
    try {
      const body = { list_ids: listIds };
      if (wsPlayerId != null) body.ws_player_id = wsPlayerId;
      if (playerId != null) body.player_id = playerId;
      if (tsCoachId != null) body.ts_coach_id = tsCoachId;
      if (status) body.status = status;
      if (note != null && note !== "") body.note = note;
      const res = await authApi.post(`${BASE}/save-from-profile/`, body);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // ── Snapshots (read-only shared-list view) ───────────────────────────────────
  getSnapshot: async (id, signal) => {
    try {
      const res = await authApi.get(`${BASE}/snapshots/${id}/`, { signal });
      return { success: true, data: res.data };
    } catch (error) {
      if (isCancel(error)) throw error;
      return fail(error);
    }
  },

  // Pull a shared shortlist onto the requester's OWN club radar (mirror of the
  // agent snapshot save; Marins Finding 2, 2026-08-21). Empty listIds → the
  // BE defaults to My Radar. Additive — nothing is ever removed.
  saveSnapshotToRadar: async (id, listIds = []) => {
    try {
      const res = await authApi.post(`${BASE}/snapshots/${id}/save-to-radar/`, {
        list_ids: listIds,
      });
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },
};

export default clubRadarService;
